import {
  Box, Button, SidebarModal, Text,
} from '@domain-group/suit-theme'
import { digObject } from '@domain-group/javascript-utils'

import type { UseNewOrderPayload } from '@modals/NewOrderModal'
import type { PublicationEditionModel, ProviderPriceListProductUnlockModel } from '@models/types'

import ProductOptionsSelector from '@components/ProductOptionsSelector'

import CategorySelector from '@modals/NewOrderModal/components/CategorySelector'
import ProductSelector from '@modals/NewOrderModal/components/ProductSelector'
import PublicationEditionSelector from '@modals/NewOrderModal/components/PublicationEditionSelector'
import PublicationLiftoverSelector from '@modals/NewOrderModal/components/PublicationLiftoverSelector'
import PublicationSelector from '@modals/NewOrderModal/components/PublicationSelector'
import ScreenContent from '@modals/NewOrderModal/components/ScreenContent'
import ScreenHeader from '@modals/NewOrderModal/components/ScreenHeader'

import type { BuildPublicationOrderPayload } from '../../functions/buildPublicationOrder'

type BuildAndCreateOrderParams = ReturnType<UseNewOrderPayload['callbacks']['buildPublicationOrder']> & {
  callbacks: {
    createCampaignQuoteItems: UseNewOrderPayload['callbacks']['createCampaignQuoteItems'],
    createCampaignQuoteTemplateItems: UseNewOrderPayload['callbacks']['createCampaignQuoteTemplateItems'],
    createOrders: UseNewOrderPayload['callbacks']['createOrders'],
  },
  itemType: 'quoteItem' | 'quoteTemplateItem',
  payload: BuildPublicationOrderPayload['payload'],
}

const buildAndCreateOrder = (params: BuildAndCreateOrderParams) => {
  const {
    callbacks: {
      createCampaignQuoteItems,
      createCampaignQuoteTemplateItems,
      createOrders,
    },
    itemType,
    payload,
  } = params

  if (itemType === 'quoteItem'){
    // @ts-expect-error TS2345
    return createCampaignQuoteItems('publication', payload)
  }

  if (itemType === 'quoteTemplateItem'){
    // @ts-expect-error TS2345
    return createCampaignQuoteTemplateItems('publication', payload)
  }

  return createOrders(payload)
}

interface PublicationFlowProps {
  callbacks: {
    addItem: VoidFunction,
    closeModal: VoidFunction,
  },
  newOrderPayload: UseNewOrderPayload,
}

const PublicationFlow = (props: PublicationFlowProps) => {
  const {
    callbacks: {
      addItem,
      closeModal,
    },
    newOrderPayload: {
      callbacks: {
        buildPublicationOrder,
        createCampaignQuoteItems,
        createCampaignQuoteTemplateItems,
        createOrders,
        resetState,
        selectCategoryId,
        selectLiftoverId,
        selectProviderPriceListItemId,
        selectProviderPriceListItemOptionValue,
        selectPublicationEditionId,
        selectPublicationId,
        selectServiceProductId,
      },
      creating,
      currentPriceList,
      filteredCategories,
      filteredProviderPriceListItems,
      filteredProviderPriceListProductUnlocks,
      filteredPublicationEditions,
      filteredPublications,
      filteredServiceProducts,
      hasFilteredCategories,
      loadingPriceLists,
      loadingPublicationEditions,
      loadingPublications,
      selectedAgency,
      selectedCampaign,
      selectedOptionsString,
      selectedPriceListItem,
      selectedPublication,
      selectedPublicationEditionIds,
      selectedService,
      selectedServiceProduct,
      state: {
        selectedCategoryId,
        selectedLiftoverIds,
        selectedProviderId,
        selectedProviderPriceListItemId,
        selectedProviderPriceListItemOptions,
        selectedPublicationId,
        selectedServiceProductId,
      },
      validOrder,
    },
  } = props

  const publicationProductOptionsEnabled = digObject(selectedPublication, 'options.product_options', 'false') === 'true'

  const addItemPayload = {
    callbacks: {
      action: (actionParams: ReturnType<UseNewOrderPayload['callbacks']['buildPublicationOrder']>) => (
        // @ts-expect-error TS2345
        buildAndCreateOrder({
          ...actionParams,
          callbacks: {
            createCampaignQuoteItems,
            createCampaignQuoteTemplateItems,
            createOrders,
          },
        })
      ),
      afterAction: closeModal,
      buildActionParams: () => {
        const buildPublicationOrderPayload = buildPublicationOrder()
        return [buildPublicationOrderPayload]
      },
    },
    toastText: 'Item Created',
  }

  return (
    <>
      {/*
       // @ts-expect-error TS2741 */}
      <ScreenHeader
        callbacks={{
          goBack: () => resetState(),
        }}
      >
        <ScreenHeader.Title>
          Newspaper
        </ScreenHeader.Title>

        <ScreenHeader.Subtitle>
          {selectedPublication.id ? `from ${selectedPublication.title}` : 'Select Publication'}
        </ScreenHeader.Subtitle>
      </ScreenHeader>

      <ScreenContent>
        {!selectedPublicationId && (
          <PublicationSelector
            callbacks={{
              selectPublication: publication => selectPublicationId(publication.id),
            }}
            // @ts-expect-error TS2322
            loading={loadingPublications}
            publications={filteredPublications}
          />
        )}

        {!!selectedProviderId && hasFilteredCategories && (
          <CategorySelector
            callbacks={{ selectCategoryId }}
            // @ts-expect-error TS2322
            selectedCategoryId={selectedCategoryId}
            serviceCategories={filteredCategories}
          />
        )}

        {!!selectedProviderId && (!!selectedCategoryId || !hasFilteredCategories) && (
          <ProductSelector
            callbacks={{ selectServiceProductId }}
            // @ts-expect-error TS2322
            currentPriceList={currentPriceList}
            loadingPriceLists={loadingPriceLists}
            // @ts-expect-error TS2322
            selectedServiceProductId={selectedServiceProductId}
            serviceProducts={filteredServiceProducts}
          />
        )}

        {!!selectedServiceProductId && publicationProductOptionsEnabled && (
          <ProductOptionsSelector
            callbacks={{
              selectProviderPriceListItemId,
              // @ts-expect-error TS2322
              selectProviderPriceListItemOptionValue,
            }}
            providerPriceListItems={filteredProviderPriceListItems}
            quantityFormFieldBoxProps={{ marginTop: 'small' }}
            // @ts-expect-error TS2322
            selectedProviderPriceListItemId={selectedProviderPriceListItemId}
            // @ts-expect-error TS2322
            selectedProviderPriceListItemOptions={selectedProviderPriceListItemOptions}
          />
        )}

        {((publicationProductOptionsEnabled && selectedProviderPriceListItemId)
          || (!publicationProductOptionsEnabled && !!selectedServiceProductId)) && (
          <PublicationEditionSelector
            callbacks={{
              selectPublicationEdition: (publicationEdition: PublicationEditionModel) => (
                selectPublicationEditionId(publicationEdition?.id)
              ),
            }}
            loading={loadingPublicationEditions}
            publicationEditions={filteredPublicationEditions}
            selectedPublication={selectedPublication}
            selectedPublicationEditionIds={selectedPublicationEditionIds}
          />
        )}

        {!!selectedPublicationEditionIds.length && !!filteredProviderPriceListProductUnlocks.length && (
          // @ts-expect-error TS2741
          <PublicationLiftoverSelector
            callbacks={{
              selectLiftover: (providerPriceListProductUnlock: ProviderPriceListProductUnlockModel) => {
                selectLiftoverId(providerPriceListProductUnlock.id)
              },
            }}
            providerPriceListProductUnlocks={filteredProviderPriceListProductUnlocks}
            selectedLiftoverIds={selectedLiftoverIds}
          />
        )}
      </ScreenContent>

      {validOrder && (
        <SidebarModal.Footer>
          <Box flexDirection="column" width="auto">
            <Text color="bodyFontColor" fontSize="medium">
              {selectedPublicationEditionIds.length === 1
                ? '1 Edition'
                : `${selectedPublicationEditionIds.length} Editions`}
            </Text>

            {!!selectedLiftoverIds.length && (
              <Text color="bodyFontLightColor" fontSize="small" marginTop="small">
                {selectedLiftoverIds.length === 1
                  ? '1 Liftover'
                  : `${selectedLiftoverIds.length} Liftovers`}
              </Text>
            )}
          </Box>

          <Box justifyContent="flex-end" marginLeft="auto" width="auto">
            <Button
              buttonStyle="primaryCreate"
              loading={creating}
              loadingBubbleColor="white"
              // @ts-expect-error TS2554
              onClick={() => addItem(addItemPayload)}
              size="medium"
              // @ts-expect-error TS2322
              width="auto"
            >
              Add Item
            </Button>
          </Box>
        </SidebarModal.Footer>
      )}
    </>
  )
}

export default PublicationFlow
